/**
 * Booking Query Hook
 * 
 * React Query hook for fetching booking details by ID.
 * 
 * ## Features
 * - Type-safe query with ApiResult handling
 * - Automatic caching and refetching
 * - Loading and error states
 * 
 * @module booking/hooks
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~/lib/query/keys';
import { cachePresets } from '~/lib/query/cachePresets';
import { bookingService } from '../api/bookingService';

export interface UseBookingQueryOptions {
  /** Booking ID to fetch */
  bookingId: number | null;
  
  /** Whether the query is enabled (default: true if bookingId exists) */
  enabled?: boolean;
}

/**
 * Fetch booking by ID
 * 
 * Returns the full React Query result with a convenience `booking` field.
 * 
 * @example
 * ```tsx
 * // Simple usage - convenience field
 * const { booking, isLoading, error } = useBookingQuery({
 *   bookingId: 184,
 * });
 * 
 * if (isLoading) return <div>Loading...</div>;
 * if (error) return <div>Error: {error.message}</div>;
 * if (!booking) return <div>Booking not found</div>;
 * 
 * return (
 *   <div>
 *     <h1>Booking #{booking.id}</h1>
 *     <p>Bay: {booking.bay?.name}</p>
 *   </div>
 * );
 * 
 * // Advanced usage - access React Query features
 * const { booking, isFetching, refetch, dataUpdatedAt } = useBookingQuery({
 *   bookingId: 184,
 * });
 * ```
 */
export function useBookingQuery({
  bookingId,
  enabled = true,
}: UseBookingQueryOptions) {
  const query = useQuery({
    // Booking detail uses a non-club-scoped key because booking IDs are globally
    // unique. This guarantees a single cache entry regardless of whether clubId
    // is available (post-creation flow vs. direct confirmation URL visit).
    queryKey: bookingId
      ? queryKeys.bookings.detail(bookingId)
      : queryKeys.disabled.bookings(),
    queryFn: async () => {
      if (!bookingId) {
        throw new Error('Booking ID is required');
      }

      const result = await bookingService.getBookingById(bookingId);
      
      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch booking');
      }
      
      return result.payload;
    },
    // The API only needs bookingId in the path — no clubId required.
    enabled: enabled && bookingId !== null,
    ...cachePresets.stable,
  });

  return {
    ...query,
    booking: query.data ?? null,
  };
}
